<template>
  <div class="bulk-delete-list">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">Bulk Delete Jobs</h1>
        <p class="page-subtitle">
          Manage and monitor your bulk content deletion jobs
        </p>
      </div>
      <div class="header-actions">
        <b-button
          variant="outline-secondary"
          size="md"
          @click="fetchAll"
          :disabled="loading"
          class="refresh-btn"
        >
          <b-icon
            icon="arrow-clockwise"
            :animation="loading ? 'spin' : ''"
          ></b-icon>
          <span class="ml-2">Refresh</span>
        </b-button>
        <b-button
          variant="primary"
          size="md"
          to="/bulk-delete/new"
          class="create-btn"
        >
          <b-icon icon="plus-circle" class="mr-2"></b-icon>
          New Job
        </b-button>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-grid">
      <div class="stat-card stat-active">
        <div class="stat-icon-wrapper">
          <b-icon icon="play-circle-fill" font-scale="2"></b-icon>
        </div>
        <div class="stat-details">
          <div class="stat-value">{{ stats.activeJobs }}</div>
          <div class="stat-label">Active Jobs</div>
        </div>
      </div>

      <div class="stat-card stat-completed">
        <div class="stat-icon-wrapper">
          <b-icon icon="check-circle-fill" font-scale="2"></b-icon>
        </div>
        <div class="stat-details">
          <div class="stat-value">{{ stats.completedJobs }}</div>
          <div class="stat-label">Completed Jobs</div>
        </div>
      </div>

      <div class="stat-card stat-deleted">
        <div class="stat-icon-wrapper">
          <b-icon icon="trash-fill" font-scale="2"></b-icon>
        </div>
        <div class="stat-details">
          <div class="stat-value">{{ stats.totalDeleted }}</div>
          <div class="stat-label">Total Deleted</div>
        </div>
      </div>

      <div class="stat-card stat-failed">
        <div class="stat-icon-wrapper">
          <b-icon icon="exclamation-circle-fill" font-scale="2"></b-icon>
        </div>
        <div class="stat-details">
          <div class="stat-value">{{ stats.totalFailed }}</div>
          <div class="stat-label">Failed Deletions</div>
        </div>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="filter-card">
      <div class="filter-header">
        <h5 class="filter-title">
          <b-icon icon="funnel" class="mr-2"></b-icon>
          Filters
        </h5>
      </div>
      <div class="filter-content">
        <b-form-group label="Status" label-for="statusFilter" class="mb-0">
          <b-form-select
            id="statusFilter"
            v-model="statusFilter"
            :options="statusOptions"
            @change="fetchJobs"
            class="custom-select"
          ></b-form-select>
        </b-form-group>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading && jobs.length === 0" class="loading-container">
      <b-spinner variant="primary" class="loading-spinner"></b-spinner>
      <p class="mt-3 text-muted">Loading jobs...</p>
    </div>

    <!-- Jobs List -->
    <div v-else-if="jobs.length > 0" class="jobs-list">
      <div v-for="job in jobs" :key="job.jobId" class="job-card">
        <div class="job-main">
          <div class="job-info">
            <h3 class="job-name">{{ job.pageName || "Unknown Page" }}</h3>
            <p class="job-id">{{ job.jobId }}</p>
            <div class="job-meta">
              <span class="meta-item">
                <b-icon icon="clock" class="mr-1"></b-icon>
                {{ formatDate(job.createdAt) }}
              </span>
              <span v-if="job.completedAt" class="meta-item">
                <b-icon icon="check-circle" class="mr-1"></b-icon>
                {{ formatDate(job.completedAt) }}
              </span>
            </div>
          </div>

          <div class="job-badges">
            <div class="badge-item">
              <span class="badge-label">Content Type</span>
              <b-badge
                :variant="getContentTypeBadge(job.contentType)"
                class="custom-badge"
              >
                <b-icon
                  :icon="getContentTypeIcon(job.contentType)"
                  class="mr-1"
                ></b-icon>
                {{ job.contentType }}
              </b-badge>
            </div>
            <div class="badge-item">
              <span class="badge-label">Status</span>
              <b-badge
                :variant="getStatusVariant(job.status)"
                class="custom-badge status-badge"
              >
                <b-icon :icon="getStatusIcon(job.status)" class="mr-1"></b-icon>
                {{ job.status }}
              </b-badge>
            </div>
          </div>

          <div class="job-progress">
            <div class="progress-header">
              <span class="progress-label">Progress</span>
              <span class="progress-stats">
                {{ job.totalDeleted }} / {{ job.totalScheduled }}
                <span v-if="job.totalFailed > 0" class="failed-count">
                  ({{ job.totalFailed }} failed)
                </span>
              </span>
            </div>
            <b-progress
              :value="getProgress(job)"
              :variant="getProgressVariant(job.status)"
              height="12px"
              class="custom-progress"
            ></b-progress>
          </div>

          <div class="job-actions">
            <b-button
              variant="primary"
              @click="viewDetails(job.jobId)"
              class="action-button"
              v-b-tooltip.hover
              title="View Details"
            >
              <b-icon icon="eye" class="mr-1"></b-icon>
              Details
            </b-button>
            <b-button
              v-if="job.status === 'Scheduled' || job.status === 'Running'"
              variant="warning"
              @click="pauseJob(job.jobId)"
              class="action-button"
              v-b-tooltip.hover
              title="Pause Job"
            >
              <b-icon icon="pause-fill"></b-icon>
            </b-button>
            <b-button
              v-if="job.status !== 'Completed' && job.status !== 'Failed'"
              variant="danger"
              @click="cancelJob(job.jobId)"
              class="action-button"
              v-b-tooltip.hover
              title="Cancel Job"
            >
              <b-icon icon="x-circle-fill"></b-icon>
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-state-icon">
        <b-icon icon="inbox" font-scale="4"></b-icon>
      </div>
      <h3 class="empty-state-title">No Jobs Found</h3>
      <p class="empty-state-text">
        {{
          statusFilter !== "all"
            ? "No jobs match the selected filter."
            : "You haven't created any bulk delete jobs yet."
        }}
      </p>
      <b-button
        variant="primary"
        to="/bulk-delete/new"
        size="lg"
        class="empty-state-button"
      >
        <b-icon icon="plus-circle" class="mr-2"></b-icon>
        Create Your First Job
      </b-button>
    </div>
  </div>
</template>

<style scoped>
.bulk-delete-list {
  padding: 0;
  background: #f5f7fa;
  min-height: 100vh;
}

/* Page Header */
.page-header {
  background: white;
  padding: 2rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.header-content {
  flex: 1;
  min-width: 250px;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  color: #6b7280;
  margin-bottom: 0;
  font-size: 0.95rem;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.refresh-btn,
.create-btn {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.6rem 1.25rem;
  transition: all 0.2s;
}

.refresh-btn:hover,
.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #374151;
}

.loading-spinner {
  width: 4rem;
  height: 4rem;
}

/* Statistics Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 0 2rem;
}

/* Statistics Cards */
.stat-card {
  background: white;
  border-radius: 16px;
  padding: 1.75rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.stat-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-active .stat-icon-wrapper {
  background: #3b82f6;
  color: white;
}

.stat-completed .stat-icon-wrapper {
  background: #10b981;
  color: white;
}

.stat-deleted .stat-icon-wrapper {
  background: #f59e0b;
  color: white;
}

.stat-failed .stat-icon-wrapper {
  background: #ef4444;
  color: white;
}

.stat-details {
  flex: 1;
}

.stat-value {
  font-size: 2.25rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.95rem;
  color: #6b7280;
  font-weight: 500;
}

/* Filter Card */
.filter-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem 2rem;
  margin: 0 2rem 2rem 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.filter-header {
  margin-bottom: 1rem;
}

.filter-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
}

.custom-select {
  border-radius: 8px;
  border: 1px solid #d1d5db;
  padding: 0.625rem 1rem;
  font-size: 0.95rem;
}

.custom-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Jobs List */
.jobs-list {
  padding: 0 2rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.job-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.job-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.job-main {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr auto;
  gap: 2rem;
  align-items: center;
}

.job-info {
  min-width: 0;
}

.job-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-id {
  font-family: "Courier New", monospace;
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.job-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.meta-item {
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
}

.job-badges {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.badge-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.badge-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.custom-badge {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  width: fit-content;
}

.status-badge {
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.job-progress {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.progress-stats {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 600;
}

.failed-count {
  color: #ef4444;
}

.custom-progress {
  border-radius: 50px;
  overflow: hidden;
  background-color: #f3f4f6;
}

.custom-progress >>> .progress-bar {
  border-radius: 50px;
  transition: width 0.6s ease;
}

.job-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.action-button {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s;
  white-space: nowrap;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.empty-state-icon {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  color: #9ca3af;
}

.empty-state-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}

.empty-state-text {
  color: #6b7280;
  font-size: 1rem;
  margin-bottom: 2rem;
  max-width: 400px;
}

.empty-state-button {
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
}

/* Status Badge Colors */
.badge-warning {
  background: #f59e0b;
  color: white;
}

.badge-danger {
  background: #ef4444;
  color: white;
}

.badge-success {
  background: #10b981;
  color: white;
}

.badge-info {
  background: #3b82f6;
  color: white;
}

.badge-primary {
  background: #8b5cf6;
  color: white;
}
</style>

<script>
import axios from "axios";

export default {
  data() {
    return {
      jobs: [],
      stats: {
        activeJobs: 0,
        completedJobs: 0,
        totalDeleted: 0,
        totalFailed: 0,
      },
      loading: false,
      refreshInterval: null,
      statusFilter: "all",
      statusOptions: [
        { value: "all", text: "All Status" },
        { value: "Fetching", text: "Fetching" },
        { value: "Scheduled", text: "Scheduled" },
        { value: "Running", text: "Running" },
        { value: "Completed", text: "Completed" },
        { value: "Failed", text: "Failed" },
        { value: "Paused", text: "Paused" },
      ],
    };
  },

  mounted() {
    this.fetchAll();
    this.startAutoRefresh();
  },

  beforeDestroy() {
    this.stopAutoRefresh();
  },

  methods: {
    async fetchAll() {
      await Promise.all([this.fetchJobs(), this.fetchStats()]);
    },

    async fetchJobs() {
      const isInitialLoad = this.jobs.length === 0;
      if (isInitialLoad) {
        this.loading = true;
      }

      try {
        const response = await axios.get("/api/bulk-delete/jobs");
        let jobs = response.data.jobs || [];

        if (this.statusFilter !== "all") {
          jobs = jobs.filter((job) => job.status === this.statusFilter);
        }

        this.jobs = jobs;
      } catch (error) {
        const errorMessage =
          error.response?.data?.error || error.message || "Unknown error";
        this.$bvToast.toast(`Failed to fetch jobs: ${errorMessage}`, {
          title: "Error",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchStats() {
      try {
        const response = await axios.get("/api/bulk-delete/stats");
        this.stats = response.data;
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    },

    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        const hasActiveJobs = this.jobs.some(
          (job) =>
            job.status === "Running" ||
            job.status === "Scheduled" ||
            job.status === "Fetching",
        );
        if (hasActiveJobs) {
          this.fetchAll();
        }
      }, 10000);
    },

    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },

    viewDetails(jobId) {
      this.$router.push(`/bulk-delete/${jobId}`);
    },

    async pauseJob(jobId) {
      try {
        await axios.post(`/api/bulk-delete/pause/${jobId}`);
        this.$bvToast.toast("Job has been paused successfully", {
          title: "Job Paused",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });
        this.fetchAll();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message;
        this.$bvToast.toast(`Failed to pause job: ${errorMessage}`, {
          title: "Pause Failed",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
        });
      }
    },

    async cancelJob(jobId) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Are you sure you want to cancel this job? This will stop all scheduled deletions and cannot be undone.",
        {
          title: "Cancel Bulk Delete Job",
          okVariant: "danger",
          okTitle: "Yes, Cancel Job",
          cancelTitle: "No, Keep Job",
          centered: true,
        },
      );

      if (!confirmed) return;

      try {
        await axios.post(`/api/bulk-delete/cancel/${jobId}`);
        this.$bvToast.toast("Job has been cancelled successfully", {
          title: "Job Cancelled",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });
        this.fetchAll();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message;
        this.$bvToast.toast(`Failed to cancel job: ${errorMessage}`, {
          title: "Cancellation Failed",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
        });
      }
    },

    getStatusVariant(status) {
      const variants = {
        Fetching: "info",
        Scheduled: "primary",
        Running: "success",
        Completed: "success",
        Failed: "danger",
        Paused: "warning",
      };
      return variants[status] || "secondary";
    },

    getStatusIcon(status) {
      const icons = {
        Fetching: "cloud-download",
        Scheduled: "clock",
        Running: "play-circle-fill",
        Completed: "check-circle-fill",
        Failed: "x-circle-fill",
        Paused: "pause-circle-fill",
      };
      return icons[status] || "circle";
    },

    getContentTypeBadge(contentType) {
      const badges = {
        videos: "primary",
        posts: "info",
        both: "success",
      };
      return badges[contentType] || "secondary";
    },

    getContentTypeIcon(contentType) {
      const icons = {
        videos: "camera-video-fill",
        posts: "file-text-fill",
        both: "grid-fill",
      };
      return icons[contentType] || "circle";
    },

    getProgress(job) {
      if (job.totalScheduled === 0) return 0;
      return (job.totalDeleted / job.totalScheduled) * 100;
    },

    getProgressVariant(status) {
      if (status === "Completed") return "success";
      if (status === "Failed") return "danger";
      if (status === "Paused") return "warning";
      return "primary";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return date.toLocaleString(undefined, options);
    },
  },
};
</script>
